import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import type { SetURLSearchParams } from "react-router-dom";

import type { LayoutNode } from "./layout";

interface UseChartFocusOptions {
	employeeMap: Map<string, unknown>;
	employeeIdFromUrl: string | null;
	fullEmployeesLength: number;
	positions: Map<string, LayoutNode>;
	offsetX: number;
	offsetY: number;
	centerOn: (x: number, y: number, scale?: number, duration?: number) => void;
	expandToNode: (nodeId: string) => void;
	wasDrag: () => boolean;
	setSearchParams: SetURLSearchParams;
	fitOnNextRef: RefObject<boolean>;
	navTargetRef: RefObject<string | null>;
}

export function useChartFocus({
	employeeMap,
	employeeIdFromUrl,
	fullEmployeesLength,
	positions,
	offsetX,
	offsetY,
	centerOn,
	expandToNode,
	wasDrag,
	setSearchParams,
	fitOnNextRef,
	navTargetRef,
}: UseChartFocusOptions) {
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
	const lastUrlFocusRef = useRef<string | null>(null);

	useEffect(() => {
		if (highlightedNodeId === null) return;
		const timer = setTimeout(() => setHighlightedNodeId(null), 2800);
		return () => clearTimeout(timer);
	}, [highlightedNodeId]);

	const setEmployeeSearchParam = useCallback(
		(employeeId: string | null) => {
			setSearchParams(
				(previous) => {
					const next = new URLSearchParams(previous);
					if (employeeId) {
						next.set("employeeId", employeeId);
					} else {
						next.delete("employeeId");
					}
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	const highlightNode = useCallback((nodeId: string) => {
		setHighlightedNodeId(nodeId);
	}, []);

	const focusEmployee = useCallback(
		(employeeId: string, options?: { syncUrl?: boolean }) => {
			if (!employeeMap.has(employeeId)) return;

			setSelectedNodeId(employeeId);
			highlightNode(employeeId);
			fitOnNextRef.current = false;
			if (options?.syncUrl !== false) {
				setEmployeeSearchParam(employeeId);
			}

			const position = positions.get(employeeId);
			if (position) {
				centerOn(position.x + position.width / 2 + offsetX, position.y + position.height / 2 + offsetY, 1.15);
				return;
			}

			navTargetRef.current = employeeId;
			expandToNode(employeeId);
		},
		[
			centerOn,
			employeeMap,
			expandToNode,
			fitOnNextRef,
			highlightNode,
			navTargetRef,
			offsetX,
			offsetY,
			positions,
			setEmployeeSearchParam,
		]
	);

	useEffect(() => {
		if (!employeeIdFromUrl || fullEmployeesLength === 0) return;
		if (lastUrlFocusRef.current === employeeIdFromUrl) return;
		lastUrlFocusRef.current = employeeIdFromUrl;
		focusEmployee(employeeIdFromUrl, { syncUrl: false });
	}, [employeeIdFromUrl, focusEmployee, fullEmployeesLength]);

	const clearFocus = useCallback(() => {
		setSelectedNodeId(null);
		setHighlightedNodeId(null);
		lastUrlFocusRef.current = null;
		setEmployeeSearchParam(null);
	}, [setEmployeeSearchParam]);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") clearFocus();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [clearFocus]);

	const handleNodeClick = useCallback(
		(employeeId: string) => {
			if (wasDrag()) return;
			setSelectedNodeId(employeeId);
			setEmployeeSearchParam(employeeId);
		},
		[setEmployeeSearchParam, wasDrag]
	);

	return {
		selectedNodeId,
		highlightedNodeId,
		focusEmployee,
		clearFocus,
		handleNodeClick,
	};
}
